import React, { useState } from 'react';

// Plain SVG charts. Validated series colours: billed blue, paid teal, third amber, fourth purple.
export const SERIES = ['#2456b8', '#1a9a8f', '#b8740f', '#9b4fb5'];
export const STATUS_COLORS = { paid: '#1e8e5a', pending: '#b8740f', partial_paid: '#2456b8', denied: '#c0392b', appealed: '#9b4fb5' };

function niceMax(v) {
  if (!v || v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * p;
}

function Legend({ series, colors }) {
  if (series.length < 2) return null;
  return (
    <div className="ops-chart__legend">
      {series.map((s, i) => <span key={s}><i style={{ background: colors[i] }} />{s}</span>)}
    </div>
  );
}

export function ChartCard({ title, subtitle, children, table, action }) {
  const [view, setView] = useState('chart');
  return (
    <section className="ops-panel">
      <div className="ops-panel__head">
        <div className="ops-panel__title" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <h2>{title}</h2>
          {subtitle && <span className="ops-panel__count">{subtitle}</span>}
        </div>
        <div className="ops-actions">
          {action}
          <div className="ops-chart__toggle" role="tablist" aria-label="View as">
            <button type="button" className={view === 'chart' ? 'is-active' : ''} onClick={() => setView('chart')}>Chart</button>
            <button type="button" className={view === 'table' ? 'is-active' : ''} onClick={() => setView('table')}>Table</button>
          </div>
        </div>
      </div>
      <div className={`ops-panel__body${view === 'table' ? ' ops-panel__body--flush' : ''}`}>
        {view === 'chart' ? children : table}
      </div>
    </section>
  );
}

export function useTip() {
  const [tip, setTip] = useState(null);
  const onMove = (e, content) => {
    const r = e.currentTarget.closest('.ops-chart-wrap').getBoundingClientRect();
    setTip({ x: e.clientX - r.left, y: e.clientY - r.top, content });
  };
  const clear = () => setTip(null);
  const node = tip ? <div className="ops-tip" style={{ left: tip.x, top: tip.y }}>{tip.content}</div> : null;
  return { onMove, clear, node };
}

// categories: string[]; series: [{ name, values: number[] }]; format: fn(number) for tooltip and axis
export function GroupedBar({ categories, series, colors = SERIES, format = v => v, height = 240 }) {
  const { onMove, clear, node } = useTip();
  const W = 720, H = height, padL = 54, padR = 10, padT = 10, padB = 28;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const max = niceMax(Math.max(0, ...series.flatMap(s => s.values)));
  const groupW = innerW / Math.max(1, categories.length);
  const gap = 2;
  const barW = Math.max(3, (groupW * 0.7 - gap * (series.length - 1)) / series.length);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * max);
  const y = v => padT + innerH - (v / max) * innerH;
  const empty = series.every(s => s.values.every(v => !v));

  return (
    <div className="ops-chart ops-chart-wrap">
      <Legend series={series.map(s => s.name)} colors={colors} />
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Grouped bar chart">
        <g className="grid">
          {ticks.map(t => <line key={t} x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} />)}
        </g>
        <g className="axis">
          {ticks.map(t => <text key={t} x={padL - 8} y={y(t) + 4} textAnchor="end">{format(t)}</text>)}
          <line x1={padL} x2={W - padR} y1={y(0)} y2={y(0)} />
        </g>
        {categories.map((c, ci) => {
          const gx = padL + ci * groupW + (groupW - (barW * series.length + gap * (series.length - 1))) / 2;
          return (
            <g key={c}>
              {series.map((s, si) => {
                const v = s.values[ci] || 0;
                const h = Math.max(0, y(0) - y(v));
                return (
                  <rect key={s.name} className="bar" x={gx + si * (barW + gap)} y={y(v)} width={barW} height={h}
                    rx={h > 4 ? 3 : 0} fill={colors[si]}
                    onMouseMove={e => onMove(e, <><b>{c}</b> · {s.name}: {format(v)}</>)} onMouseLeave={clear} />
                );
              })}
              <text x={gx + (barW * series.length + gap * (series.length - 1)) / 2} y={H - 8} textAnchor="middle">{c}</text>
            </g>
          );
        })}
        {empty && <text x={W / 2} y={H / 2} textAnchor="middle">No data for this range</text>}
      </svg>
      {node}
    </div>
  );
}

// rows: [{ label, values: number[] }]; series: string[]
export function HBar({ rows, series, colors = SERIES, format = v => v }) {
  const { onMove, clear, node } = useTip();
  const W = 720, padL = 150, padR = 70, rowH = 16 * series.length + 14, padT = 6, padB = 6;
  const H = padT + padB + rowH * Math.max(1, rows.length);
  const innerW = W - padL - padR;
  const max = niceMax(Math.max(0, ...rows.flatMap(r => r.values)));
  const x = v => padL + (v / max) * innerW;

  return (
    <div className="ops-chart ops-chart-wrap">
      <Legend series={series} colors={colors} />
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Horizontal bar chart">
        {rows.map((r, ri) => {
          const top = padT + ri * rowH + 6;
          return (
            <g key={r.label}>
              <text x={padL - 10} y={top + (16 * series.length) / 2 + 4} textAnchor="end" style={{ fill: '#1f2d3d', fontSize: 12 }}>{r.label}</text>
              {series.map((s, si) => {
                const v = r.values[si] || 0;
                const w = Math.max(0, x(v) - padL);
                return (
                  <g key={s}>
                    <rect className="bar" x={padL} y={top + si * 16} width={w} height={14} rx={w > 4 ? 3 : 0} fill={colors[si]}
                      onMouseMove={e => onMove(e, <><b>{r.label}</b> · {s}: {format(v)}</>)} onMouseLeave={clear} />
                    <text x={x(v) + 6} y={top + si * 16 + 11}>{format(v)}</text>
                  </g>
                );
              })}
            </g>
          );
        })}
        {rows.length === 0 && <text x={W / 2} y={H / 2} textAnchor="middle">No data</text>}
      </svg>
      {node}
    </div>
  );
}

// segments: [{ key, label, value, color }]
export function Donut({ segments, format = v => v, centerLabel }) {
  const { onMove, clear, node } = useTip();
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  const R = 70, r = 46, cx = 90, cy = 90;
  let angle = -Math.PI / 2;
  const arcs = segments.filter(s => s.value > 0).map(s => {
    const frac = s.value / total;
    const a0 = angle, a1 = angle + frac * Math.PI * 2 - (segments.length > 1 ? 0.03 : 0);
    angle += frac * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad, a) => [cx + rad * Math.cos(a), cy + rad * Math.sin(a)];
    const [x0, y0] = p(R, a0), [x1, y1] = p(R, a1), [x2, y2] = p(r, a1), [x3, y3] = p(r, a0);
    return { ...s, d: `M${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${r},${r} 0 ${large} 0 ${x3},${y3} Z`, frac };
  });
  return (
    <div className="ops-chart ops-chart-wrap" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 180 180" style={{ width: 180, flex: '0 0 180px' }} role="img" aria-label="Donut chart">
        {arcs.map(a => (
          <path key={a.key} d={a.d} fill={a.color} className="bar"
            onMouseMove={e => onMove(e, <><b>{a.label}</b>: {format(a.value)} ({Math.round(a.frac * 100)}%)</>)} onMouseLeave={clear} />
        ))}
        {total === 0 && <circle cx={cx} cy={cy} r={(R + r) / 2} fill="none" stroke="#e3e7ec" strokeWidth={R - r} />}
        <text x={cx} y={cy - 2} textAnchor="middle" style={{ fill: '#1f2d3d', fontSize: 20, fontWeight: 600 }}>{format(total)}</text>
        {centerLabel && <text x={cx} y={cy + 16} textAnchor="middle">{centerLabel}</text>}
      </svg>
      <ul className="ops-list" style={{ flex: 1, minWidth: 180 }}>
        {segments.map(s => (
          <li key={s.key} style={{ padding: '6px 0' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: 'inline-block' }} />{s.label}</span>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{format(s.value)}</b>
          </li>
        ))}
      </ul>
      {node}
    </div>
  );
}

export function SimpleTable({ columns, rows }) {
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead><tr>{columns.map(c => <th key={c.key} className={c.num ? 'num' : ''}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map(c => <td key={c.key} className={c.num ? 'num' : ''}>{c.render ? c.render(r[c.key], r) : r[c.key]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
